import React, { useEffect, useState, useCallback } from 'react';
import ChartTooltip from '../components/ChartTooltip';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getChannels, parseReport } from '../api';
import ChartCard from '../components/ChartCard';

const COLORS = ['#38bdf8', '#818cf8', '#34d399', '#f472b6', '#fb923c', '#fbbf24', '#a78bfa'];

export default function Acquisition({ propertyId, startDate, endDate }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await getChannels(propertyId, startDate, endDate);
      const rows = parseReport(res.data)
        .map(r => ({ ...r, bounceRate: parseFloat((r.bounceRate * 100).toFixed(1)), engagementRate: parseFloat((r.engagementRate * 100).toFixed(1)) }))
        .sort((a, b) => b.sessions - a.sessions);
      setData(rows);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [propertyId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, color: 'var(--text3)', textAlign: 'center' }}>Loading acquisition data...</div>;

  const pieData = data.map(d => ({ name: d.sessionDefaultChannelGroup, value: d.sessions }));

  return (
    <div style={styles.grid}>
      <ChartCard title="Sessions by Channel" subtitle="Traffic source distribution" span={2}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="sessionDefaultChannelGroup" tick={{ fill: '#475569', fontSize: 10, angle: -20, textAnchor: 'end' }} />
            <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="sessions" name="Sessions" radius={[4, 4, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Traffic Share" subtitle="Channel pie breakdown">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={pieData} cx="50%" cy="50%" outerRadius={110} dataKey="value" nameKey="name">
              {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, color: 'var(--text2)' }} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Active Users by Channel" subtitle="User quality per source">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="sessionDefaultChannelGroup" tick={{ fill: '#475569', fontSize: 9, angle: -20, textAnchor: 'end' }} />
            <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="activeUsers" name="Active Users" fill="#38bdf8" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Engagement Rate by Channel" subtitle="Which channels engage best?">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="sessionDefaultChannelGroup" tick={{ fill: '#475569', fontSize: 9, angle: -20, textAnchor: 'end' }} />
            <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} unit="%" />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="engagementRate" name="Engagement %" fill="#34d399" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Channel Details Table" subtitle="Full breakdown" span={4}>
        <div style={styles.table}>
          <div style={styles.thead}>
            {['Channel', 'Sessions', 'Active Users', 'Engagement %', 'Bounce %'].map(h => <span key={h}>{h}</span>)}
          </div>
          {data.map((r, i) => (
            <div key={i} style={styles.trow}>
              <span style={{ color: COLORS[i % COLORS.length], fontWeight: 600 }}>{r.sessionDefaultChannelGroup}</span>
              <span>{r.sessions?.toLocaleString()}</span>
              <span>{r.activeUsers?.toLocaleString()}</span>
              <span style={{ color: r.engagementRate > 50 ? 'var(--success)' : 'var(--warning)' }}>{r.engagementRate}%</span>
              <span style={{ color: r.bounceRate < 40 ? 'var(--success)' : 'var(--danger)' }}>{r.bounceRate}%</span>
            </div>
          ))}
        </div>
      </ChartCard>
    </div>
  );
}

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },
  table: { display: 'flex', flexDirection: 'column' },
  thead: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '8px 12px', fontSize: 10, color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', background: 'var(--surface2)', borderRadius: 8 },
  trow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '12px 12px', fontSize: 13, color: 'var(--text2)', borderBottom: '1px solid var(--border2)' },
};
